import { useState } from "react";
import { motion } from "framer-motion";
import { fadeIn } from "../components/common/motionFrameVarients";
import Footer from "../components/common/Footer";
import HighlightText from "../components/core/HomePage/HighlightText";
import CTAButton from "../components/core/HomePage/Button";

// Sample data for tiers and rewards
const affiliateTiers = [
  {
    level: "Bronze",
    commission: "10%",
    requirements: "1-10 referrals per month",
    benefits: ["Basic dashboard access", "Monthly payments", "Referral tracking"]
  },
  {
    level: "Silver",
    commission: "15%",
    requirements: "11-30 referrals per month",
    benefits: ["Advanced analytics", "Bi-weekly payments", "Custom referral links", "Promotional materials"]
  },
  {
    level: "Gold",
    commission: "20%",
    requirements: "31+ referrals per month",
    benefits: ["Priority support", "Weekly payments", "Custom promotional campaigns", "Exclusive webinars", "Early access to new courses"]
  }
];

// Sample FAQ data
const faqs = [
  {
    question: "How does the affiliate program work?",
    answer: "Our affiliate program allows you to earn commissions by referring students to Kode Vedic courses. You'll receive a unique referral link to share. When someone makes a purchase using your link, you earn a commission based on your tier level."
  },
  {
    question: "When and how do I get paid?",
    answer: "Payments are processed according to your tier level (monthly, bi-weekly, or weekly). We support multiple payment methods including PayPal, bank transfer, and cryptocurrency."
  },
  {
    question: "Can I promote any course?",
    answer: "Yes, you can promote any course or bundle available on our platform. Some special courses or limited-time offerings may have different commission rates, which will be clearly communicated."
  },
  {
    question: "How long does the referral cookie last?",
    answer: "Our tracking cookies last for 60 days. This means if someone clicks your link but purchases within 60 days later, you'll still receive credit for the referral."
  },
  {
    question: "Can I track my performance?",
    answer: "Yes, you'll have access to a comprehensive dashboard that shows clicks, conversions, earnings, and other important metrics in real-time."
  }
];

// Sample featured affiliate testimonials
const testimonials = [
  {
    name: "Alex Johnson",
    role: "Tech Blogger",
    image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
    quote: "The Kode Vedic affiliate program has been a game-changer for my blog. The high-quality courses make promotion easy, and the commission rates are among the best in the industry."
  },
  {
    name: "Sophia Chen",
    role: "YouTube Educator",
    image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
    quote: "I've been promoting Kode Vedic courses to my audience for over a year now. The tracking is reliable, payments are always on time, and my subscribers love the content."
  }
];

function AffiliatesPage() {
  const [activeTab, setActiveTab] = useState("about");
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.1 }}
      className="w-full overflow-hidden bg-richblack-900 text-white"
    >
      {/* Hero Section */}
      <div className="relative w-full h-[70vh]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-richblack-900/90 flex items-center justify-center">
          <div className="text-center px-4 w-11/12 max-w-maxContent">
            <motion.h1
              variants={fadeIn("up", 0.2)}
              className="text-4xl md:text-6xl font-bold text-white mb-6"
            >
              Earn While You <HighlightText text="Share Knowledge" />
            </motion.h1>
            <motion.p
              variants={fadeIn("up", 0.4)}
              className="text-richblack-200 text-lg md:text-xl max-w-4xl mx-auto mb-8"
            >
              Join our affiliate program and earn up to 20% commission for every successful referral
            </motion.p>
            <motion.div variants={fadeIn("up", 0.6)} className="flex flex-col sm:flex-row justify-center gap-4">
              <CTAButton
                active={true}
                linkto="/affiliates/register"
              >
                Become an Affiliate
              </CTAButton>
              <CTAButton
                active={false}
                linkto="#how-it-works"
              >
                Learn More
              </CTAButton>
            </motion.div>
          </div>
        </div>
        <img 
          src="https://images.pexels.com/photos/7688460/pexels-photo-7688460.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
          alt="People collaborating" 
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Navigation Tabs */}
      <div className="bg-richblack-800 sticky top-16 z-10">
        <div className="w-11/12 max-w-maxContent mx-auto flex overflow-x-auto">
          <button 
            onClick={() => setActiveTab("about")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "about" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            Program Overview
          </button>
          <button 
            onClick={() => setActiveTab("how-it-works")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "how-it-works" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            How It Works
          </button>
          <button 
            onClick={() => setActiveTab("commission")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "commission" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            Commission Structure
          </button>
          <button 
            onClick={() => setActiveTab("faq")}
            className={`px-6 py-4 font-medium whitespace-nowrap ${activeTab === "faq" ? "border-b-2 border-yellow-50 text-yellow-50" : "text-richblack-300"}`}
          >
            FAQ
          </button>
        </div>
      </div>
      
      {/* Program Overview */}
      <section id="about" className={`w-11/12 max-w-maxContent mx-auto py-16 scroll-mt-24 ${activeTab !== "about" ? "hidden" : ""}`}>
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              About Our <HighlightText text="Affiliate Program" />
            </h2>
            <p className="text-richblack-300 mb-6">
              The Kode Vedic Affiliate Program is designed for content creators, educators, influencers, and anyone passionate about tech education. By promoting our high-quality coding courses, you can earn substantial commissions while helping others advance their tech careers.
            </p>
            <p className="text-richblack-300 mb-6">
              Our program offers some of the most competitive commission rates in the e-learning industry, with a tiered structure that rewards your growth and dedication.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <CTAButton
                active={true}
                linkto="/affiliates/register"
              >
                Apply Now
              </CTAButton>
              <CTAButton
                active={false}
                linkto="/affiliates/resources"
              >
                Marketing Resources
              </CTAButton>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img 
              src="https://images.pexels.com/photos/5926397/pexels-photo-5926397.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
              alt="Affiliate Marketing" 
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
        
        {/* Key Benefits */}
        <motion.div
          variants={fadeIn("up", 0.3)}
          className="mt-16"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            Program <HighlightText text="Benefits" />
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-richblack-800 p-6 rounded-xl">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold mb-3">High Commission Rates</h3>
              <p className="text-richblack-300">
                Earn up to 20% on every course purchase made through your referral link. Our tiered structure rewards top performers.
              </p>
            </div>
            
            <div className="bg-richblack-800 p-6 rounded-xl">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-xl font-bold mb-3">Transparent Tracking</h3>
              <p className="text-richblack-300">
                Access a robust dashboard with real-time data on clicks, conversions, and earnings. Always know how your promotions are performing.
              </p>
            </div>
            
            <div className="bg-richblack-800 p-6 rounded-xl">
              <div className="text-4xl mb-4">🛠️</div>
              <h3 className="text-xl font-bold mb-3">Marketing Resources</h3>
              <p className="text-richblack-300">
                Get access to banners, email templates, social media content, and other promotional materials to boost your success.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
      
      {/* How It Works */}
      <section id="how-it-works" className={`bg-richblack-800 py-16 scroll-mt-24 ${activeTab !== "how-it-works" ? "hidden" : ""}`}>
        <div className="w-11/12 max-w-maxContent mx-auto">
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How It <HighlightText text="Works" />
            </h2>
            <p className="text-richblack-300 max-w-3xl mx-auto">
              Getting started with our affiliate program is easy. Follow these simple steps to begin earning commissions.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <motion.div
              variants={fadeIn("up", 0.3)}
              className="bg-richblack-700 p-6 rounded-xl relative"
            >
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-richblack-900 font-bold">1</div>
              <h3 className="text-xl font-bold mb-3 mt-2">Apply</h3>
              <p className="text-richblack-300">
                Fill out our simple application form to tell us about yourself and how you plan to promote our courses.
              </p>
            </motion.div>
            
            <motion.div
              variants={fadeIn("up", 0.4)}
              className="bg-richblack-700 p-6 rounded-xl relative"
            >
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-richblack-900 font-bold">2</div>
              <h3 className="text-xl font-bold mb-3 mt-2">Get Approved</h3>
              <p className="text-richblack-300">
                Our team will review your application and get back to you within 2-3 business days.
              </p>
            </motion.div>
            
            <motion.div
              variants={fadeIn("up", 0.5)}
              className="bg-richblack-700 p-6 rounded-xl relative"
            >
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-richblack-900 font-bold">3</div>
              <h3 className="text-xl font-bold mb-3 mt-2">Promote</h3>
              <p className="text-richblack-300">
                Share your unique referral links through your website, social media, email list, or other channels.
              </p>
            </motion.div>
            
            <motion.div
              variants={fadeIn("up", 0.6)}
              className="bg-richblack-700 p-6 rounded-xl relative"
            >
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-richblack-900 font-bold">4</div>
              <h3 className="text-xl font-bold mb-3 mt-2">Earn</h3>
              <p className="text-richblack-300">
                Earn commissions for every sale that comes through your referral links. Get paid according to your tier schedule.
              </p>
            </motion.div>
          </div>
          
          <motion.div
            variants={fadeIn("up", 0.7)}
            className="mt-16 text-center"
          >
            <h3 className="text-2xl font-bold mb-6">Ready to Start Earning?</h3>
            <CTAButton
              active={true}
              linkto="/affiliates/register"
            >
              Join Our Affiliate Program
            </CTAButton>
          </motion.div>
        </div>
      </section>
      
      {/* Commission Structure */}
      <section id="commission" className={`w-11/12 max-w-maxContent mx-auto py-16 scroll-mt-24 ${activeTab !== "commission" ? "hidden" : ""}`}>
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Commission <HighlightText text="Structure" />
          </h2>
          <p className="text-richblack-300 max-w-3xl mx-auto">
            Our tiered commission structure rewards your performance and growth as an affiliate partner.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {affiliateTiers.map((tier, index) => (
            <motion.div
              key={index}
              variants={fadeIn("up", 0.3 + index * 0.1)}
              className={`p-8 rounded-xl border ${
                tier.level === "Bronze" ? "border-yellow-700 bg-richblack-800" : 
                tier.level === "Silver" ? "border-richblack-300 bg-richblack-800" : 
                "border-yellow-50 bg-richblack-700"
              }`}
            >
              <div className={`text-2xl font-bold mb-1 ${
                tier.level === "Bronze" ? "text-yellow-700" : 
                tier.level === "Silver" ? "text-richblack-300" : 
                "text-yellow-50"
              }`}>
                {tier.level}
              </div>
              <div className="text-4xl font-bold mb-6">{tier.commission}</div>
              <p className="text-richblack-300 mb-6">{tier.requirements}</p>
              
              <h4 className="font-semibold mb-3">Benefits:</h4>
              <ul className="space-y-2 mb-8">
                {tier.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-50">✓</span>
                    <span className="text-richblack-100">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <CTAButton
                active={tier.level === "Gold"}
                linkto="/affiliates/register"
                className="w-full text-center"
              >
                Apply Now
              </CTAButton>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* FAQ Section */}
      <section id="faq" className={`bg-richblack-800 py-16 scroll-mt-24 ${activeTab !== "faq" ? "hidden" : ""}`}>
        <div className="w-11/12 max-w-maxContent mx-auto">
          <motion.div
            variants={fadeIn("up", 0.2)}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked <HighlightText text="Questions" />
            </h2>
          </motion.div>
          
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                variants={fadeIn("up", 0.3 + index * 0.1)}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className={`w-full text-left p-5 flex justify-between items-center ${activeFaq === index ? "bg-richblack-700 rounded-t-lg" : "bg-richblack-900 rounded-lg"}`}
                >
                  <h3 className="font-medium text-lg">{faq.question}</h3>
                  <span className="text-xl">
                    {activeFaq === index ? "−" : "+"}
                  </span>
                </button>
                
                {activeFaq === index && (
                  <div className="bg-richblack-700 p-5 rounded-b-lg">
                    <p className="text-richblack-100">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="w-11/12 max-w-maxContent mx-auto py-16">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Successful <HighlightText text="Affiliates" />
          </h2>
          <p className="text-richblack-300 max-w-3xl mx-auto">
            Hear from content creators who are already succeeding with our affiliate program
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeIn("up", 0.3 + index * 0.1)}
              className="bg-richblack-800 p-6 rounded-xl"
            >
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name} 
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h3 className="font-semibold">{testimonial.name}</h3>
                  <p className="text-sm text-richblack-300">{testimonial.role}</p>
                </div>
              </div>
              <p className="text-richblack-100 italic">"{testimonial.quote}"</p>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="bg-gradient-to-r from-richblack-900 to-richblack-800 py-16">
        <motion.div
          variants={fadeIn("up", 0.2)}
          className="w-11/12 max-w-maxContent mx-auto text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to <HighlightText text="Start Earning?" />
          </h2>
          <p className="text-richblack-300 mb-8 max-w-2xl mx-auto">
            Join our affiliate program today and start earning commissions by promoting our high-quality tech education courses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton
              active={true}
              linkto="/affiliates/register"
            >
              Become an Affiliate
            </CTAButton>
            <CTAButton
              active={false}
              linkto="/contact"
            >
              Contact Support
            </CTAButton>
          </div>
        </motion.div>
      </section>
      <Footer />
    </motion.div>
  );
}

export default AffiliatesPage;